/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from "react";
import Datatable from "../Common/Datatable";
import { getColumns, initialSort } from "./meetingsTableConfig";
import useGlobalContext from "../../context/useGlobalContext";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { downloadBlob, getBaseRoute, normalizeToFullDay, getUniqueAccounts, parseBlobError, prepareExportDownload } from "../../utils/utils";
import { Box, Typography, type SxProps, type Theme } from "@mui/material";
import { getUserAttributes } from "../../utils/auth";
import UserTeam from "../Analytics/UserTeam";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./Meetings.Style";
import { InteractionsColumnVisibility, UserInfo, UserTeamData } from "../../utils/types";
import UserInteractionsService from "../../services/interactions/Interactions";
import "../../styles/common.css";
import { format, subDays } from "date-fns";
import FilterPill from "../Common/FilterPill.tsx";
import SearchIcon from "../../assets/common/search-icon.svg";
import CloseIcon from '@mui/icons-material/Close';
import ShareInteractionDialog from "../MeetingDetailsPage/ShareInteractionDialog.tsx";
import ColumnFilter, { ExclusiveColumnFilterContext } from "../Common/customFilter.tsx";
import ClientService from "../../services/clients/ClientService";
import DownloadReport from "../Common/DownloadReport";
import useInteractionSection, {
  getInteractionWindows,
  InteractionSectionKey,
  InteractionSort,
} from "./useInteractionSection";
import InteractionAccordion from "./InteractionAccordion";
import CollateralService from "../../services/collaterals/CollateralService.ts";


const generateRows = (meetings: any, isTeamMeeting: boolean, selectedTab: any, startEndTime: any,
  newUserTeamData: any, sharedWithMe: boolean, setSelectedInteractionIds: React.Dispatch<React.SetStateAction<string[]>>
,setOpenDialog:React.Dispatch<React.SetStateAction<boolean>>, showShareIcon: boolean, sharedByMe: boolean, ParentPageIdentifier: string,
  externalFilter:boolean, pageSize:number, pageNumber:number, filterState?: {
    meetingFilterDateRange: { preset: string; startDate: string; endDate: string; };
    searchQuery: string;
    debouncedSearch: string;
    rating: string;
    currentRoute: string;
    taggedAccountSubOrgId?: string[];
    taggedOpportunityId?: string[];
    sort?: InteractionSort;
    interactionSection?: InteractionSectionKey;
  } ) =>
  meetings.map((item: any) => ({
    id: item.interactionId,
    title: item.interactionTitle,
    start_time: item.startTime,
    end_time: item.endTime,
    duration: item.duration,
    no_of_attendees: item.noOfAttendees,
    is_external: item.isExternal,
    attendees: item.attendees,
    organizer: item.empEmail,
    sentiment_score: item.sentimentScore,
    meeting_sentiment: item.overallSentiment,
    join_link: item.joinLink,
    role_in_interaction: item.roleInInteraction,
    response_status: item.responseStatus,
    profile_org_ids: item.profileOrgRelationshipIds,
    rating_source: item.ratingSource,
    isTeamMeeting: isTeamMeeting,
    selectedTab: selectedTab,
    startEndTime: startEndTime,
    newUserTeamData: newUserTeamData,
    botStatus: item.botStatus,
    clientDomains: item.clientDomains,
    sharedWithMe: sharedWithMe,
    sharedDate: item.sharedDate,
    abstractiveScore: item.abstractiveScore,
    setSelectedInteractionIds: setSelectedInteractionIds,
    setOpenDialog:setOpenDialog,
    showShareIcon: showShareIcon,
    sharedByMe: sharedByMe,
    is_manager: item.isManager,
    ParentPageIdentifier: ParentPageIdentifier,
    externalFilter: externalFilter,
    pageSize: pageSize,
    pageNumber: pageNumber,
    actual_duration: item.actualDuration,
    selfTalkTime: item.selfTalkTime,
    sentimentSummary: item.sentimentSummary,
    filterState: filterState,
    hasAsset: item.hasAsset,
    scoringType: item.scoringType,
    isPresentAsset: item.isPresentAsset,
    isPreviousAsset: item.isPreviousAsset,
    isAdhocInteraction: item.isAdhocInteraction,
    ...(() => {
      const list = [...(item.accountOpportunities || [])].sort(
        (a: any, b: any) => (b?.opportunityName?.trim() ? 1 : 0) - (a?.opportunityName?.trim() ? 1 : 0)
      );
      const accs = Array.from(new Set(list.map((o: any) => o.accountName?.trim()).filter(Boolean)));
      const opps = Array.from(new Set(list.map((o: any) => o.opportunityName?.trim()).filter(Boolean)));
      return {
        account: accs,
        opportunityName: opps,
      };
    })(),
  }));

  interface DateRangeValue {
    preset: string;
    startDate: string;
    endDate: string;
  }

  export const parseDateRangeParam = (param: string | null): DateRangeValue | null => {
    if (!param) return null;
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (dateRegex.test(param)) {
      const parts = param.split("-");
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const dateObj = new Date(year, month, day);
      if (
        !isNaN(dateObj.getTime()) &&
        dateObj.getFullYear() === year &&
        dateObj.getMonth() === month &&
        dateObj.getDate() === day
      ) {
        const { startDate, endDate } = normalizeToFullDay(dateObj, dateObj);
        return {
          preset: "custom",
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        };
      }
    }
    return null;
  };

  interface MeetingParams {
    setInteractionDateRange?: React.Dispatch<React.SetStateAction<DateRangeValue>>
  }
  const Meetings = ({setInteractionDateRange}: MeetingParams) => { 
  const location = useLocation();
  const navigate = useNavigate();
  const loggedInEmail = getUserAttributes()?.email;
  const { calendar, timeRange, userTeamData, accountDetails, impersonationDetails, featureMap } = useGlobalContext();
  const shareInteractionEnabled = featureMap?.share_interaction === true;
  const isRatingColumnEnabled = featureMap?.enable_rating_column === true;
  const highradiusInteractionColumnsEnabled = featureMap?.highradius_interaction_columns === true;
  const wbrReportDownloadEnabled = featureMap?.wbr_report_download === true;
  const wbrDepartmentReportDownloadEnabled = featureMap?.wbr_department_report_download === true;
  const noSentimentInRatingColumnEnabled = featureMap?.no_sentiment_in_rating_column === true;
  const isImpersonating = impersonationDetails?.isImpersonating === true;
  const impersonatingUserEmail = impersonationDetails?.impersonatingUserEmail;
  const baseEmail = (isImpersonating && impersonatingUserEmail) ? impersonatingUserEmail : loggedInEmail;
  const isExternalUser = accountDetails?.isExternalUser ?? false;
  const { showSnackbar } = useDisplaySnackbar();
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [external, setExternal] = useState<boolean>(location.state?.externalFilter ?? true);
  const [newUserTeamData, setNewUserTeamData] = useState<UserTeamData | null>(null);
  const [selectedInteractionIds, setSelectedInteractionIds] = useState<any[]>([]);
  const incomingFilterState = location.state?.filterState;
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const ParentPageIdentifier = "Meeting_page";
  const [rating, setRating] = useState<string>(incomingFilterState?.rating || "");
  const isHighRadiusUser = loggedInEmail?.endsWith("@highradius.com");

  const getTabFromPath = (pathname: string): string => {
    if (pathname.includes("/meetings/my")) return "myInteraction";
    if (pathname.includes("/meetings/team")) return "teamInteraction";
    if (pathname.includes("/meetings/shared-with-me")) return "sharedWithMe";
    if (pathname.includes("/meetings/shared-by-me")) return "sharedByMe";
    return "myInteraction"; // default
  };
  const selectedTab = getTabFromPath(location.pathname);
  const showShareIcon = true;
  const handleNewUserTeamData = (data: {
    currentUser: string;
    clickedUserInfo?: UserInfo;
    currentManager?: UserInfo;
    departments: any[];
    subordinates: UserInfo[];
    managerOptions: UserInfo[];
  }) => {
    setNewUserTeamData(data);
  };
  const loggedInProfileOrgId = accountDetails?.profileOrgId;
  const impersonatingUserPorId = impersonationDetails?.impersonatingUserPorId;
  const accessLevel = impersonationDetails?.accessType;

  const [selectedUser, setSelectedUser] = useState<any | null>(null); // State for selected user
  const [_department, setDepartment] = useState("");
  const { startEndMeetingTime } = useGlobalContext();
  const sectionRowsRef = useRef<{
    setPastRows: React.Dispatch<React.SetStateAction<any[]>> | null;
    setUpcomingRows: React.Dispatch<React.SetStateAction<any[]>> | null;
  }>({ setPastRows: null, setUpcomingRows: null });
  const resetSectionPagesRef = useRef<() => void>(() => {});
  const restoredSection: InteractionSectionKey =
    incomingFilterState?.interactionSection === "upcoming" ? "upcoming" : "past";
  const restoredPageSize = location.state?.pageSize ?? 50;
  const restoredPageNumber = location.state?.pageNumber ?? 1;
  const restoredSort: InteractionSort = incomingFilterState?.sort ?? initialSort;
  const [expandedSections, setExpandedSections] = useState({
    upcoming: restoredSection === "upcoming",
    past: restoredSection !== "upcoming",
  });

  const sharedWithMe = selectedTab === "sharedWithMe" ? true : false;
  const sharedByMe = selectedTab === "sharedByMe" ? true : false;
  const getInitialDateRange = (): DateRangeValue => {
    const searchParams = new URLSearchParams(location.search);
    const dateRangeParam = searchParams.get("dateRange");
    const parsed = parseDateRangeParam(dateRangeParam);
    if (parsed) {
      return parsed;
    }

    if (incomingFilterState?.meetingFilterDateRange) {
      return incomingFilterState.meetingFilterDateRange;
    }
  
    const stored = sessionStorage.getItem("meetings_date_range_filter");
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return { preset: "currentWeek", startDate: "", endDate: "" };
      }
    }
  
    return { preset: "currentWeek", startDate: "", endDate: "" };
  };
  const [meetingFilterDateRange, setMeetingFilterDateRange] = useState<DateRangeValue>(getInitialDateRange);
  const [searchQuery, setSearchQuery] = useState(incomingFilterState?.searchQuery ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(incomingFilterState?.debouncedSearch ?? "");
  const { mutateAsync: postAdhocInteractionBot } =UserInteractionsService.usePostAdhocInteractionBot();

  const handleSaveMeetingTitle = async (row: any, newTitle: string) => {
    try {
      setShowSpinner(true);
      await postAdhocInteractionBot({
        interactionId: row.id,
        title: newTitle,
        profileOrgId: loggedInProfileOrgId || "",
        meetingUrl: "",
      });
      const updateTitle = (prev: any[]) =>
        prev.map((r) => (r.id === row.id ? { ...r, title: newTitle } : r));
      sectionRowsRef.current.setPastRows?.(updateTitle);
      sectionRowsRef.current.setUpcomingRows?.(updateTitle);
      showSnackbar("success", "Interaction title updated.");
    } catch (error: any) {
      showSnackbar("error", error?.response?.data?.message || "Failed to update interaction title.");
    } finally {
      setShowSpinner(false);
    }
  };
  const [taggedAccountIds, setTaggedAccountIds] = useState<string[]>(
    incomingFilterState?.taggedAccountSubOrgId || []
  );
  const [taggedOpportunityIds, setTaggedOpportunityIds] = useState<string[]>(
    incomingFilterState?.taggedOpportunityId || []
  );
  const [openFilterColumn, setOpenFilterColumn] = useState<string | null>(null);

  const [clientList, setClientList] = useState<any[]>([]);
  const [opportunityList, setOpportunityList] = useState<any[]>([]);
 
    const opportunityOptions = useMemo(() => {
    return (opportunityList || [])
      .filter((opp: any) => opp.id && opp.opportunityName?.trim())
      .map((opp: any) => ({
        label: opp.opportunityName?.trim(),
        value: opp.id,
      }));
  }, [opportunityList]);

    const accountOptions = useMemo(() => {
      return getUniqueAccounts({ accountList: clientList }).map(
        (option: any) => ({
          ...option,
          type: "account" as const,
        })
      );
    }, [clientList]);

    const updateColumnFilter = (key: string, value: any) => {
    if (key === "account") {
      handleAccountFilterChange(value);
    } else if (key === "opportunityName") {
      handleOpportunityFilterChange(value);
    }
  };

  const columns = useMemo(
    () =>
      getColumns(
        selectedTab,
        handleSaveMeetingTitle,
        {
          account: taggedAccountIds,
          opportunityName: taggedOpportunityIds,
        },
        updateColumnFilter,
        accountOptions,
        openFilterColumn,
        setOpenFilterColumn,
        true,
        opportunityOptions,
        noSentimentInRatingColumnEnabled
      ),
    [
      selectedTab,
      taggedAccountIds,
      taggedOpportunityIds,
      accountOptions,
      opportunityOptions,
      openFilterColumn,
      noSentimentInRatingColumnEnabled,
    ]
  );
  const prevTab = useRef(selectedTab);
  const lastProcessedDateRange = useRef<string | null>(
    (() => {
      const searchParams = new URLSearchParams(location.search);
      const dateRangeParam = searchParams.get("dateRange");
      if (dateRangeParam && parseDateRangeParam(dateRangeParam)) {
        return dateRangeParam;
      }
      return null;
    })()
  );

  const clearDateRangeParam = () => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has("dateRange")) {
      searchParams.delete("dateRange");
      const newSearch = searchParams.toString();
      navigate({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : "",
      }, { replace: true });
    }
  };

  const currentRoute = getBaseRoute(location.pathname);
  const [selectedLabel, setSelectedLabel] = useState({
    label: "Shared Date",
    value: "SHARED_DATE",
  });
    
    const {
    isLoading: isAccountsLoading,
    isFetching: isAccountsFetching,
    isError: isAccountsError,
    error: accountsError,
    data: accountsData,
  } = ClientService.useGetActiveClients(
    true,
    undefined,
    undefined,
    undefined,
    undefined,
    ["all"],
  );

  useEffect(() => {
    if (accountsData) {
      const accountsList = (accountsData?.data ?? [])
      setClientList(accountsList);
    } else if (isAccountsError) {
      showSnackbar("error", accountsError?.message || "Failed to load Active Clients data.");
    }
  }, [accountsData, isAccountsError, accountsError, isAccountsLoading, isAccountsFetching]);




  const {
    isLoading: isOpportunitiesLoading,
    isFetching: isOpportunitiesFetching,
    isError: isOpportunitiesError,
    error: opportunitiesError,
    data: opportunitiesData,
  } = UserInteractionsService.useGetActiveOpportunities(
    highradiusInteractionColumnsEnabled,
    isImpersonating && impersonatingUserPorId
      ? {
          impersonatingPorId: impersonatingUserPorId,
          accessLevel: accessLevel,
        }
      : undefined
  );

  useEffect(() => {
    if (opportunitiesData) {
      const payload = opportunitiesData?.data ?? opportunitiesData;
      const oppRawList = Array.isArray(payload?.opportunities)
        ? payload.opportunities
        : [];
      setOpportunityList(
        oppRawList.map((opp: any) => ({
          id: opp?.id,
          opportunityName: opp?.opportunityName ?? "",
          subOrgId: opp?.subOrgId,
          salesforceAccountId: opp?.salesforce_account_id,
        }))
      );
    } else if (isOpportunitiesError) {
      showSnackbar("error", opportunitiesError?.message || "Failed to load opportunities data.");
    }
  }, [opportunitiesData, isOpportunitiesError, opportunitiesError, isOpportunitiesLoading, isOpportunitiesFetching]);


  const handleOpportunityFilterChange = (selectedArr: any) => {
    const nextArr = Array.isArray(selectedArr) ? selectedArr : selectedArr ? [selectedArr] : [];
    setTaggedOpportunityIds(nextArr);
    resetSectionPagesRef.current();
  };

  const handleAccountFilterChange = (selectedArr: any) => {
    const nextArr = Array.isArray(selectedArr) ? selectedArr : selectedArr ? [selectedArr] : [];
    setTaggedAccountIds(nextArr);
    resetSectionPagesRef.current();
  };

  const { data: selectedInteractionCollateralData } = CollateralService.useGetInteractionCollateral(
    {
      interactionId: selectedInteractionIds[0] || "",
      impersonatingPorId: impersonatingUserPorId,
      accessLevel: accessLevel,
    },
    openDialog && !!selectedInteractionIds[0]
  );

  const filterState = {
    meetingFilterDateRange,
    searchQuery,
    debouncedSearch,
    rating,
    currentRoute,
    taggedAccountSubOrgId: taggedAccountIds,
      ...(highradiusInteractionColumnsEnabled && taggedOpportunityIds.length > 0 && {
    taggedOpportunityId: taggedOpportunityIds,
  }),
  };
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 1000); // 1 second debounce delay
  
    return () => {
      clearTimeout(handler); // cleanup previous timer on every keystroke
    };
  }, [searchQuery]);
  
  useEffect(() => {
    if (setInteractionDateRange) {
      setInteractionDateRange(meetingFilterDateRange);
    }
    if (
      meetingFilterDateRange?.startDate &&
      meetingFilterDateRange?.endDate
    ) {
      sessionStorage.setItem(
        "meetings_date_range_filter",
        JSON.stringify(meetingFilterDateRange)
      );
    }
  }, [meetingFilterDateRange, setInteractionDateRange]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const dateRangeParam = searchParams.get("dateRange");
    if (dateRangeParam) {
      if (dateRangeParam !== lastProcessedDateRange.current) {
        const parsed = parseDateRangeParam(dateRangeParam);
        if (parsed) {
          lastProcessedDateRange.current = dateRangeParam;
          setMeetingFilterDateRange(parsed);
        }
      }
    } else {
      lastProcessedDateRange.current = null;
    }
  }, [location.search]);
  
  const isValidDate = (date: any): boolean => {
    if (!date) return false;
    const parsed = typeof date === "string" ? new Date(date) : date;
    return parsed instanceof Date && !isNaN(parsed.getTime());
  };

  const baseProfileOrgId = (isImpersonating && impersonatingUserPorId) ? impersonatingUserPorId : loggedInProfileOrgId;
  const validEmployeeEmail =
    (selectedTab === "myInteraction"
      ? baseProfileOrgId
      : newUserTeamData?.currentUser || selectedUser?.employeePorId) || baseProfileOrgId || "";
  const shouldCallApi = Boolean(
  isValidDate(meetingFilterDateRange?.startDate) &&
  isValidDate(meetingFilterDateRange?.endDate) &&
  (
    isExternalUser === true ||
    (validEmployeeEmail)
));

  const shouldCallCount =
   selectedTab !== "sharedWithMe" && selectedTab !== "sharedByMe";
  const isSharedTab = selectedTab === "sharedWithMe" || selectedTab === "sharedByMe";

  const interactionWindows = useMemo(
    () =>
      getInteractionWindows(
        meetingFilterDateRange?.startDate,
        meetingFilterDateRange?.endDate,
        new Date()
      ),
    [meetingFilterDateRange?.startDate, meetingFilterDateRange?.endDate]
  );

  const interactionFilters = useMemo(
    () => ({
      debouncedSearch,
      rating,
      isHighRadiusUser: Boolean(isHighRadiusUser),
      external,
      selectedTab,
      baseProfileOrgId: baseProfileOrgId || "",
      employeePorId: validEmployeeEmail,
      dateFilterType:
        selectedTab === "sharedWithMe" || selectedTab === "sharedByMe"
          ? selectedLabel.value
          : undefined,
      impersonatingPorId: impersonatingUserPorId,
      accessLevel,
      taggedAccountIds,
      taggedOpportunityIds,
      includeOpportunityFilter: Boolean(highradiusInteractionColumnsEnabled),
    }),
    [
      debouncedSearch,
      rating,
      isHighRadiusUser,
      external,
      selectedTab,
      baseProfileOrgId,
      validEmployeeEmail,
      selectedLabel.value,
      impersonatingUserPorId,
      accessLevel,
      taggedAccountIds,
      taggedOpportunityIds,
      highradiusInteractionColumnsEnabled,
    ]
  );

  const buildSectionRows = (
    section: InteractionSectionKey,
    interactions: any[],
    sectionPageSize: number,
    sectionPageNumber: number,
    sectionSort: InteractionSort
  ) =>
    generateRows(
      interactions,
      selectedTab === "myInteraction" ? false : true,
      selectedTab,
      startEndMeetingTime,
      newUserTeamData,
      sharedWithMe,
      setSelectedInteractionIds,
      setOpenDialog,
      showShareIcon,
      sharedByMe,
      ParentPageIdentifier,
      external,
      sectionPageSize,
      sectionPageNumber,
      {
        ...filterState,
        sort: sectionSort,
        interactionSection: section,
      }
    );

  const upcomingSection = useInteractionSection({
    windowRange: isSharedTab ? null : interactionWindows.upcoming,
    enabled: shouldCallApi && !isSharedTab,
    fetchList: expandedSections.upcoming || !shouldCallCount,
    shouldCallCount,
    initialPageNumber: restoredSection === "upcoming" ? restoredPageNumber : 1,
    initialPageSize: restoredSection === "upcoming" ? restoredPageSize : 50,
    initialSort:
      restoredSection === "upcoming"
        ? restoredSort
        : { sort_field: "start_time", sort_order: "asc" },
    filters: interactionFilters,
    buildRows: (interactions, sectionPageSize, sectionPageNumber, sectionSort) =>
      buildSectionRows("upcoming", interactions, sectionPageSize, sectionPageNumber, sectionSort),
  });

  const pastSection = useInteractionSection({
    windowRange: interactionWindows.past,
    enabled: shouldCallApi,
    fetchList: expandedSections.past || !shouldCallCount,
    shouldCallCount,
    initialPageNumber: restoredSection === "past" ? restoredPageNumber : 1,
    initialPageSize: restoredSection === "past" ? restoredPageSize : 50,
    initialSort: restoredSection === "past" ? restoredSort : initialSort,
    filters: interactionFilters,
    buildRows: (interactions, sectionPageSize, sectionPageNumber, sectionSort) =>
      buildSectionRows("past", interactions, sectionPageSize, sectionPageNumber, sectionSort),
  });

  sectionRowsRef.current.setPastRows = pastSection.setRows;
  sectionRowsRef.current.setUpcomingRows = upcomingSection.setRows;
  resetSectionPagesRef.current = () => {
    upcomingSection.setPageNumber(1);
    pastSection.setPageNumber(1);
  };

  useEffect(() => {
    if (!highradiusInteractionColumnsEnabled) {
      setTaggedOpportunityIds([]);
    }
  }, [highradiusInteractionColumnsEnabled]);

  useEffect(() => {
    if (prevTab.current !== selectedTab) {
      setSearchQuery('');
      setTaggedAccountIds([]);
      setTaggedOpportunityIds([]);
      resetSectionPagesRef.current();
    }
    prevTab.current = selectedTab;
  }, [selectedTab]);

  useEffect(() => {
    if (
      location.state?.pageNumber != undefined &&
      location.state?.pageNumber != null
    ) {
      const setSectionPage =
        incomingFilterState?.interactionSection === "upcoming"
          ? upcomingSection.setPageNumber
          : pastSection.setPageNumber;
      setSectionPage(location.state.pageNumber);
    }
  }, [location.state?.pageNumber]);

  useEffect(() => {
    if (
      !isValidDate(meetingFilterDateRange?.startDate) ||
      !isValidDate(meetingFilterDateRange?.endDate)
    ) {
      return;
    }
    const hasPast = Boolean(interactionWindows.past);
    const hasUpcoming = Boolean(interactionWindows.upcoming);
    setExpandedSections((prev) => {
      if (!hasPast && !hasUpcoming) {
        if (!prev.upcoming && !prev.past) return prev;
        return { upcoming: false, past: false };
      }
      if (!hasPast) {
        if (prev.upcoming && !prev.past) return prev;
        return { upcoming: true, past: false };
      }
      if (!hasUpcoming) {
        if (!prev.upcoming && prev.past) return prev;
        return { upcoming: false, past: true };
      }
      return prev;
    });
  }, [
    meetingFilterDateRange?.startDate,
    meetingFilterDateRange?.endDate,
    interactionWindows.past,
    interactionWindows.upcoming,
  ]);

  const scoringType =
    pastSection.interactionsData?.data?.[0]?.scoringType ??
    upcomingSection.interactionsData?.data?.[0]?.scoringType;

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedInteractionIds([]);
  };

  const { mutate: downloadReport, isPending: isReportDownloadPending } =
    UserInteractionsService.useDownloadWbrReport({
      onSuccess: async (data: Blob, department: string) => {
        try {
          const file = await prepareExportDownload(data);
          const reportDateFile = format(subDays(new Date(), 1), "MMM_dd_yyyy");
          const reportFilePrefix =
            department === "sales"
              ? "Sales_Report"
              : department === "marketing"
                ? "Marketing_Report"
                : "WBR_Report";
          downloadBlob(file, `${reportFilePrefix}_${reportDateFile}.csv`);
        } catch (err: any) {
          showSnackbar("error", err.message || "Failed to download report.");
        }
      },
      onMutate: () => {
        setShowSpinner(true);
      },
      onError: async (error: any) => {
        showSnackbar("error", await parseBlobError(error));
      },
      onSettled: () => {
        setShowSpinner(false);
      },
    });

  const handleExport = (department?: string) => {
    if (!department) return;
    downloadReport(department);
  }; 
  const yesterday = format(subDays(new Date(), 1), "MMM dd, yyyy");

  const wbrReportOptions = [
    ...(wbrReportDownloadEnabled
      ? [{ label: `WBR Report - ${yesterday}`, value: "wbr" }]
      : []),

    ...(wbrDepartmentReportDownloadEnabled
      ? [
          { label: `Sales Report - ${yesterday}`, value: "sales" },
          { label: `Marketing Report - ${yesterday}`, value: "marketing" },
        ]
      : []),
  ];

  const defaultColumnVisibility = useMemo<InteractionsColumnVisibility>(() => ({
    title: true,
    sharedDate: isSharedTab,
    start_time: true,
    account: true,
    opportunityName: highradiusInteractionColumnsEnabled,
    duration: false,
    actual_duration: false,
    meeting_sentiment: scoringType === "extraction",
    abstractiveScore: isRatingColumnEnabled ? scoringType === "abstraction" : false,
    rating_source: false,
    shareIcon: shareInteractionEnabled,
  }), [isSharedTab, scoringType, isRatingColumnEnabled, shareInteractionEnabled, highradiusInteractionColumnsEnabled]);

  const [columnVisibilityOverrides, setColumnVisibilityOverrides] = useState<Partial<InteractionsColumnVisibility>>(() => {
    try {
      const saved = sessionStorage.getItem("interactionsColumnVisibility");
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const exclusiveFilterValue = useMemo(
  () => ({
    activeKey: openFilterColumn,
    setActiveKey: setOpenFilterColumn,
  }),
  [openFilterColumn, setOpenFilterColumn]
);

  const showExtractionRating = scoringType === "extraction";
  const showAbstractionRating = isRatingColumnEnabled && scoringType === "abstraction";

  const columnVisibility = useMemo<InteractionsColumnVisibility>(() => {
    const merged: InteractionsColumnVisibility = {
      ...defaultColumnVisibility,
      ...columnVisibilityOverrides,
      title: true,
      rating_source: false,
    };
    if (!isSharedTab) merged.sharedDate = false;
    if (!highradiusInteractionColumnsEnabled) merged.opportunityName = false;
    if (!shareInteractionEnabled) merged.shareIcon = false;
    merged.meeting_sentiment = showExtractionRating
      ? (columnVisibilityOverrides.meeting_sentiment ?? true)
      : false;
    merged.abstractiveScore = showAbstractionRating
      ? (columnVisibilityOverrides.abstractiveScore ?? true)
      : false;
    return merged;
  }, [
    defaultColumnVisibility,
    columnVisibilityOverrides,
    isSharedTab,
    shareInteractionEnabled,
    showExtractionRating,
    showAbstractionRating,
    highradiusInteractionColumnsEnabled,
  ]);

  useEffect(() => {
    sessionStorage.setItem("interactionsColumnVisibility", JSON.stringify(columnVisibilityOverrides));
  }, [columnVisibilityOverrides]);

  const columnVisibilityOptions = useMemo(() => ([
    { value: "title", label: "Interaction title", defaultValue: true },
    ...(isSharedTab ? [{ value: "sharedDate", label: "Shared Date" }] : []),
    { value: "start_time", label: isSharedTab ? "Interaction Date" : "Date" },
    { value: "account", label: "Account" },
    ...(highradiusInteractionColumnsEnabled ? [{ value: "opportunityName", label: "Opportunity Name" }] : []),
    { value: "duration", label: "Scheduled Duration" },
    { value: "actual_duration", label: "Actual Duration" },
    ...(showExtractionRating ? [{ value: "meeting_sentiment", label: "Rating" }] : []),
    ...(showAbstractionRating ? [{ value: "abstractiveScore", label: "Rating" }] : []),
    ...(shareInteractionEnabled ? [{ value: "shareIcon", label: "Share" }] : []),
  ]), [isSharedTab, showExtractionRating, showAbstractionRating, shareInteractionEnabled, highradiusInteractionColumnsEnabled]);

  const handleColumnVisibilityApply = (val: InteractionsColumnVisibility) => {
    const optionKeys = new Set(columnVisibilityOptions.map((option) => option.value));
    setColumnVisibilityOverrides((prev) => {
      const overrides: Partial<InteractionsColumnVisibility> = { ...prev };
      (Object.keys(defaultColumnVisibility) as (keyof InteractionsColumnVisibility)[]).forEach((key) => {
        if (key === "title" || !optionKeys.has(key)) return;
        if (val[key] !== defaultColumnVisibility[key]) {
          overrides[key] = val[key];
        } else {
          delete overrides[key];
        }
      });
      return overrides;
    });
  };

  const renderInteractionTable = (
    section: ReturnType<typeof useInteractionSection>,
    windowRange: { startTime: string; endTime: string },
    fillTable = false,
  ) => (
    <Box
      sx={
        (fillTable
          ? [
              styles["interaction-accordion__table"],
              styles["interaction-accordion__table--fill"],
            ]
          : [
              styles["interaction-accordion__table"],
              styles["interaction-accordion__table--bounded"],
            ]) as SxProps<Theme>
      }
    >
    <Datatable
      columns={columns}
      rows={section.rows}
      rowCount={section.totalRecord ?? 0}
      pageSize={section.pageSize}
      initialSort={section.sort}
      onPaginationChange={section.onPaginationChange}
      onSortingChange={section.onSortingChange}
      startEndTime={{ start_time: windowRange.startTime, end_time: windowRange.endTime }}
      timeRange={timeRange}
      calendarId={calendar?.calendar_id}
      onPageSizeChange={(newPageSize: number | null | undefined) => {
        if (newPageSize !== null && newPageSize !== undefined) {
          section.handlePageSizeChange(newPageSize);
        }
      }}
      selectedTab={selectedTab}
      columnVisibility={{
        ...columnVisibility,
        is_manager: false,
        talkTrack: false,
        momDocLink: false,
      }}
      showSpinner={section.isLoading || showSpinner}
      maxHeight="100%"
      minHeight="0"
      currentPageIndex={section.pageNumber - 1}
      pinnedColumns={{ left: ["title"] }}
      enableColumnResizing={true}
      muiTableBodyRowProps={({ row }: { row: any }) => {
        const isSelectedForShare =
          openDialog &&
          selectedInteractionIds.includes(row?.original?.id || row?.id);
        return {
          sx: {
            backgroundColor: isSelectedForShare ? "#F2EFFD !important" : "transparent",
            "& td, & th": {
              backgroundColor: isSelectedForShare ? "#F2EFFD !important" : "transparent",
            },
            "& td:before": {
              backgroundColor: isSelectedForShare ? "#F2EFFD !important" : undefined,
            },
            "& td[data-pinned='true'], & th[data-pinned='true'], & td[data-pinned='true']:before, & th[data-pinned='true']:before": {
              backgroundColor: isSelectedForShare ? "#F2EFFD !important" : undefined,
            },
            "&:hover td, &:hover th": {
              backgroundColor: isSelectedForShare ? "#F2EFFD !important" : undefined,
            },
            "&:hover td[data-pinned='true'], &:hover th[data-pinned='true'], &:hover td[data-pinned='true']:before, &:hover th[data-pinned='true']:before": {
              backgroundColor: isSelectedForShare ? "#F2EFFD !important" : undefined,
            },
          },
        };
      }}
    />
    </Box>
  );

  const renderSectionBody = (
    section: ReturnType<typeof useInteractionSection>,
    windowRange: { startTime: string; endTime: string } | null,
    emptyLabel: string,
    fillTable = false,
  ) => {
    const countKnown = section.totalRecord != null;
    const hasRows = section.rows.length > 0 || (section.totalRecord ?? 0) > 0;
    if (!windowRange || (countKnown && !section.isLoading && !hasRows)) {
      return (
        <Typography sx={styles["interaction-accordion__empty"]}>
          {emptyLabel}
        </Typography>
      );
    }
    return renderInteractionTable(section, windowRange, fillTable);
  };

  return (
    <>
      <Box
        className="flex flex-col justify-start items-start w-full"
        sx={{
          ...styles["meeting-container"],
          overflow: "visible",
        }}
      >
        <Box
        className="flex justify-between items-center w-full h-auto"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 3,
          marginBottom: "20px",
          marginTop: "0px",
        }}
      >
        {/* 🔹 LEFT SECTION — Filters */}
        <Box
          className="filters-left filter-scroll-container"
          sx={{
            flexShrink: 0,
            flexWrap: "nowrap",
          }}
        >
          <Box sx={styles["meeting-table__container"]}>
            {selectedTab === "teamInteraction" && (isImpersonating ? (impersonationDetails?.accessType === "team") : userTeamData.showTeamTab) && (
              <>
                {userTeamData.userTeamDataMessage ? (
                  <Box sx={styles["callout-message"]}>
                    <Typography variant="body1" className="callout">
                      {userTeamData.userTeamDataMessage}
                    </Typography>
                  </Box>
                ) : (
                  <Box className="user-team-dropdown" sx={{marginRight: "10px", marginTop: "-4px", flexShrink: 0, width: "max-content",}}>
                    <UserTeam
                      setDepartment={setDepartment}
                      setSelectedUser={setSelectedUser}
                      loggedInEmail={baseEmail}
                      loggedInProfileOrgId={baseProfileOrgId}
                      showDropdown={true}
                      display={false}
                      onDataUpdate={handleNewUserTeamData}
                      parent_page="Meetings-page"
                    />
                  </Box>
                )}
              </>
            )}
          </Box>

          <FilterPill
             key="meeting-date"
            selectedTab={selectedTab}
            type="date"
            label={
              selectedTab === "sharedWithMe" || selectedTab === "sharedByMe"
                ? [
                    { label: "Shared Date", value: "SHARED_DATE" },
                    { label: "Interaction Date", value: "INTERACTION_DATE" },
                  ]
                : "Date"
            }
            value={meetingFilterDateRange}
            selectedLabel={selectedLabel?.label}
            onLabelChange={(lblObj: { label: string; value: string }) =>
              setSelectedLabel(lblObj)
            }
            onChange={(val) => {
              clearDateRangeParam();
              resetSectionPagesRef.current();
              if (val && typeof val === "object" && !Array.isArray(val) && "preset" in val){
                setMeetingFilterDateRange({
                  preset: val.preset,
                  startDate: val.startDate,
                  endDate: val.endDate,
                });
              } else if (typeof val === "string") {
                setMeetingFilterDateRange((prev) => ({
                  ...prev,
                  preset: val || "",
                }));
              }
            }}
            limitFutureDays={14}
            showClearIcon={false}
          />
          <FilterPill
          selectedTab={selectedTab}
            type="select"
            label="Interaction Type"
            options={[
              { label: "External", value: "external" },
              { label: "All", value: "all" },
            ]}
            value={external ? "external" : "all"}
            onChange={(value) => {
              setExternal(value === "external");
              resetSectionPagesRef.current();
            }}
          />
          <FilterPill
          selectedTab={selectedTab}
            type="select"
            label="Rating"
            options={[
              { label: "Positive", value: "Positive", labelColour: "#32BD00" },
              { label: "Neutral", value: "Neutral", labelColour: "#FFCD2A" },
              { label: "Negative", value: "Negative", labelColour: "#F4442F"},
              { label: "Any Rating", value: "rated"},
              { label: "Not Rated", value: "notRated"},
            ]}
          value={rating}
            onChange={(value?: any) => {
              if (typeof value === "string" || value === undefined) {
                resetSectionPagesRef.current();
                setRating(value || "");
              }
            }}
          />
          {taggedAccountIds.length > 0 && (
            <FilterPill
              key="account"
              selectedTab={selectedTab}
              isLoading={isAccountsLoading}
              label="Account"
              options={accountOptions}
              type="select"
              value={taggedAccountIds}
              displayLabel="Account"
              parent_page="meetings-page"
              showClearIcon={true}
              onChange={(selectedValue) => {
                if (Array.isArray(selectedValue)) {
                  handleAccountFilterChange(selectedValue);
                } else {
                  handleAccountFilterChange(selectedValue ? [selectedValue as string] : []);
                }
              }}
            />
          )}
          {highradiusInteractionColumnsEnabled && taggedOpportunityIds.length > 0 && (
            <FilterPill
              key="opportunityname"
              selectedTab={selectedTab}
              label="Opportunity Name"
              options={opportunityOptions}
              isLoading={isOpportunitiesLoading}
              hideUntilSearchThreshold={5}
              type="select"
              value={taggedOpportunityIds}
              displayLabel="Opportunity Name"
              parent_page="meetings-page"
              showClearIcon={true}
              onChange={(selectedValue) => {
                if (Array.isArray(selectedValue)) {
                  handleOpportunityFilterChange(selectedValue);
                } else {
                  handleOpportunityFilterChange(selectedValue ? [selectedValue as string] : []);
                }
              }}
            />
          )}
        </Box>

        {/* 🔹 RIGHT SECTION — Actions */}
        <Box
          className="filters-right flex items-center gap-3"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
         <Box className="search-box-container">
            <Box className="search-input-wrapper">
              <img src={SearchIcon} alt="" className="search-icon" />
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              {searchQuery && (
                <CloseIcon
                  className="search-clear-icon"
                  sx={{ fontSize: 18, color: "#999" }}
                  onClick={() => setSearchQuery("")}
                />
              )}
            </Box>
          </Box>
          <ColumnFilter
            currentValue={columnVisibility}
            onApply={(val) => handleColumnVisibilityApply(val as InteractionsColumnVisibility)}
            options={columnVisibilityOptions}
            type="toggle"
            columnKey="columnVisibility"
            isActive={openFilterColumn === "columnVisibility"}
            onOpen={() => {
              setOpenFilterColumn("columnVisibility");
            }}
            onClose={() => setOpenFilterColumn(null)}
            showSections={{
              search: true,
              sort: false,
            }}
          />

          {selectedTab === "sharedWithMe" && wbrReportOptions.length > 0 && (
            <DownloadReport
                onDownload={handleExport}
                options={wbrReportOptions}
                disabled={isReportDownloadPending}
              />
          )}
        </Box>
      </Box>
        {(selectedTab === "myInteraction" ||
          selectedTab === "teamInteraction" ||
          selectedTab === "sharedWithMe" ||
          selectedTab === "sharedByMe") && (
          <Box className="w-full" sx={styles["interaction-sections"]}>
            <ExclusiveColumnFilterContext.Provider value={exclusiveFilterValue}>
              {!isSharedTab && (
                <InteractionAccordion
                  title="Upcoming Interactions"
                  count={upcomingSection.totalRecord}
                  expanded={expandedSections.upcoming}
                  onExpandedChange={(isExpanded) =>
                    setExpandedSections((prev) => ({ ...prev, upcoming: isExpanded }))
                  }
                >
                  {renderSectionBody(
                    upcomingSection,
                    interactionWindows.upcoming,
                    "No upcoming interactions",
                  )}
                </InteractionAccordion>
              )}
              <InteractionAccordion
                title="Past Interactions"
                count={pastSection.totalRecord}
                expanded={expandedSections.past}
                pinToTop={expandedSections.past}
                onExpandedChange={(isExpanded) =>
                  setExpandedSections((prev) => ({ ...prev, past: isExpanded }))
                }
              >
                {renderSectionBody(
                  pastSection,
                  interactionWindows.past,
                  "No past interactions",
                  true,
                )}
              </InteractionAccordion>
            </ExclusiveColumnFilterContext.Provider>
          </Box>
        )}
      </Box>
      <ShareInteractionDialog
        open={openDialog}
        onClose={handleCloseDialog}
        interactionId={selectedInteractionIds[0] || ""}
        collaterals={selectedInteractionCollateralData?.data?.collaterals || []}
        interactionTitle={
          [...upcomingSection.rows, ...pastSection.rows].find(
            (r) => r.id === selectedInteractionIds[0]
          )?.title || ""
        }
      /> 
    </>
  );
};

export default Meetings;
